import { useState, useEffect, useLayoutEffect } from "preact/hooks";
import { Suspense, lazy } from "preact/compat";
import { format, add, startOfWeek } from "date-fns";
import { debounce } from "lodash";
const Schedule = lazy(() => import("./Schedule"));
const ThankYou = lazy(() => import("./ThankYou"));
const RenderDateTimeSelection = lazy(() =>
  import("./components/RenderDateTimeSelection")
);
import ProcessingComponent from "./components/ProcessingComponent";
import {
  services,
  getApiEndpoint,
  fetchConfirmCode,
} from "./utils/commonUtils";
import useHover from "./hooks/useHoverStates";
import useWeekNavigation from "./hooks/useWeekNavigation";
import useBookingSelection from "./hooks/useBookingSelection";
import { useSubmissionStates } from "./hooks/useSubmissionStates";
import { useSlotSelection } from "./hooks/useSlotSelection";
import { useLocationContext } from "./context/LocationContext";
import useRecaptcha from "./hooks/useReCaptcha";
import { API_BASE_URL } from "./config";

// Shown when a booking fails for a reason the patient cannot fix from the
// form: no connection, or an error on our side.
const BOOKING_FAILED_MESSAGE =
  "We couldn't complete your booking. Please try again in a moment, or call the office to schedule.";

// function getNextSaturday() {
//   const today = new Date();
//   const dayOfWeek = today.getDay(); // 0 for Sunday, 1 for Monday, ..., 6 for Saturday

//   // Calculate days until next Saturday
//   const daysUntilSaturday = 6 - dayOfWeek;

//   // Calculate milliseconds until next Saturday
//   const millisecondsUntilSaturday = daysUntilSaturday * 24 * 60 * 60 * 1000;

//   // Calculate the date of next Saturday
//   const nextSaturday = new Date(today.getTime() + millisecondsUntilSaturday);

//   return nextSaturday;
// }

function Details({ locationId, onIsNextEnabled, isSubmitEnabled }) {
  // Determine the initial week start based on the current day
  const today = new Date();
  const isWeekend = today.getDay() === 0 || today.getDay() === 6; // Sunday or Saturday
  let initialWeekStart = startOfWeek(today, { weekStartsOn: 1 });

  if (isWeekend) {
    // If it's weekend, set initialWeekStart to next Monday
    initialWeekStart = add(initialWeekStart, { weeks: 1 });
  }

  // const [selectedOption, setSelectedOption] = useState("New Patient"); // Set default to New Patient
  const {
    bookingFor,
    selectedService,
    handleBookingForChange,
    handleServiceSelect,
    selectedOption,
    handleOptionChange,
  } = useBookingSelection();
  const { currentWeekStart, nextWeek, prevWeek } =
    useWeekNavigation(initialWeekStart);
  const {
    selectedSlots,
    setSelectedSlots,
    selectedSlot,
    selectedDateSlot,
    setSelectedDateSlot,
    selectedTimeSlot,
    setSelectedTimeSlot,
    setSelectedSlot,
  } = useSlotSelection();
  const [goToSchedule, setGoToSchedule] = useState(false);
  const [scheduleData, setScheduleData] = useState({});
  const {
    isSubmitted,
    setIsSubmitted,
    isProcessing,
    setIsProcessing,
    isSubmissionReady,
    setIsSubmissionReady,
    isSlotsLoading,
    setIsSlotsLoading,
    isScheduleEnabled,
    setIsScheduleEnabled,
  } = useSubmissionStates();
  const [locID, setLocId] = useState(null);
  const { hoveredService, handleMouseEnter, handleMouseLeave } = useHover();
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const {
    setUnderMaintenance,
    noSlots,
    setNoSlots,
    fetchLocationData,
    fetchLocation,
    location,
    locations,
  } = useLocationContext();
  const [osIsLoading, setOsIsLoading] = useState(false);
  // Read from the API rather than baked in at build time: this is a static
  // build, so a value compiled in could only be changed by deploying again, and
  // Ops asked for the free consult on the understanding they can stop it
  // quickly. A flag that cannot be read stays off.
  const [features, setFeatures] = useState({
    free_consult: false,
    free_consult_label: "",
  });

  // Feature flags. Failing to read them leaves everything off, which for the
  // free consult means it simply is not offered: showing it when its Ghost
  // rooms may not be set up would only produce an empty calendar.
  useEffect(() => {
    let cancelled = false;

    fetch(`${API_BASE_URL}/features`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data) setFeatures((prev) => ({ ...prev, ...data }));
      })
      .catch((e) => {
        console.error("Could not load feature flags, leaving them off:", e);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Determine which services to display
  const displayedServices = services[selectedOption][bookingFor];

  const { generateToken } = useRecaptcha();

  useLayoutEffect(() => {
    fetchLocation(locationId);
  }, []);

  useLayoutEffect(() => {
    if (location !== null) {
      setOsIsLoading(true);
    }
  }, [location]);

  useEffect(() => {
    const isNextEnabled =
      selectedOption &&
      bookingFor &&
      selectedService &&
      selectedSlots?.length > 0;
    onIsNextEnabled(isNextEnabled);
    isSubmitEnabled(isSubmitted);
    setNoSlots(false);
    setUnderMaintenance(false); // Reset under maintenance flag
    // if (locationId && selectedService) {
    //   setIsSlotsLoading(true);
    //   const endpoint = getApiEndpoint(selectedOption, bookingFor);
    //   const startDate = format(currentWeekStart, "yyyy-MM-dd");
    //   const endDate = format(add(currentWeekStart, { days: 6 }), "yyyy-MM-dd"); // 7 days later

    //   const apiUrl = `https://mintproxyninja.com/api/${endpoint}?start_date=${startDate}&end_date=${endDate}&locations[]=${locationId}`;

    // // Fetch location data using the function from context
    //   fetchLocationData(apiUrl, setIsSlotsLoading );

    // }
    if (selectedOption && bookingFor && selectedService && locationId) {
      setIsSlotsLoading(true);
      const endpoint = getApiEndpoint(
        selectedOption,
        bookingFor,
        selectedService
      );

      if (endpoint) {
        const startDate = format(currentWeekStart, "yyyy-MM-dd");
        const endDate = format(
          add(currentWeekStart, { days: 6 }), // Assuming a 7-day week
          "yyyy-MM-dd"
        );
        const apiUrl = `${API_BASE_URL}/${endpoint}?start_date=${startDate}&end_date=${endDate}&locations[]=${locationId}`;

        // Fetch location data using the function from context or directly
        fetchLocationData(apiUrl, setIsSlotsLoading);
      }
    }
  }, [
    locationId,
    selectedService,
    selectedOption,
    bookingFor,
    currentWeekStart,
    // selectedSlots,
    isSubmitted,
  ]); // Now also depends on selectedOption and bookingFor

  const isNextEnabled =
    selectedOption &&
    bookingFor &&
    selectedService &&
    selectedSlots?.length > 0;

  // The selection hook was written for radio inputs and reads the value off
  // the event. The buttons that replaced them hand it the same shape.
  const choose = (handler, value) => handler({ target: { value } });

  // A rejected submit (wrong age for the type, say) leaves its message on
  // the Schedule step. Going back is how the booking gets corrected, so the
  // message goes with it; otherwise it was still there after the fix.
  const clearSubmitError = () => {
    setShowError(false);
    setErrorMessage("");
  };

  // A picked time belongs to the type it was picked for: the same hour can
  // sit in different rooms for an adult and a child. The highlight is keyed
  // by date, time and office only, so after Back and a change of type the old
  // pick stayed selected and the booking went out with the adult rooms,
  // which the API rejects as not available. Changing the type starts the
  // pick over.
  useEffect(() => {
    setSelectedSlots(null);
    setSelectedSlot(null);
    setSelectedDateSlot(null);
    setSelectedTimeSlot(null);
  }, [selectedOption, bookingFor, selectedService]);

  const handleNextClick = () => {
    if (isNextEnabled) {
      clearSubmitError();
      setGoToSchedule(true);
      setIsScheduleEnabled(true);
    }
  };

  const handleBackClick = () => {
    clearSubmitError();
    setGoToSchedule(false);
    setIsScheduleEnabled(false);
  };

  const handleScheduleCallbackData = async (data) => {
    setScheduleData(data);
  };

  useEffect(() => {
    let isFormComplete =
      scheduleData?.firstName?.length > 0 &&
      scheduleData?.lastName?.length > 0 &&
      scheduleData?.emailValid &&
      scheduleData?.gender?.length > 0 &&
      scheduleData?.birthDate?.length === 10 &&
      scheduleData?.mobilePhone?.length >= 14 &&
      !scheduleData.isUnderage &&
      scheduleData?.hasInsurance?.length > 0; // insurance option must be selected

    // If insurance is yes, check that insuranceCarrierName is not empty
    if (scheduleData?.hasInsurance === "yes") {
      isFormComplete =
        isFormComplete && scheduleData?.insuranceCarrierName?.length > 0;
    }
    if (scheduleData?.insuranceCarrierName === "Other Carrier") {
      isFormComplete =
        isFormComplete && scheduleData?.otherInsuranceCarrierName?.length > 0;
    }

    // Assuming you have a state called setIsSubmitEnabled to control the enabled state of the Submit button
    setIsSubmissionReady(isFormComplete);
  }, [scheduleData]);

  // Calendar days from today, today being 0, the same count the web app
  // uses. The date is read as a local day: `new Date("2026-10-23")` is UTC
  // midnight, which in Chicago is the evening before, so a same-day booking
  // came out as -1 days and was left Not Contacted (MDW-245).
  function isWithinDays(dateStr, count) {
    const today = new Date();
    const [year, month, day] = dateStr.split("-").map(Number);
    const selectedDate = new Date(year, month - 1, day);
    const selectedDateUTC = new Date(
      Date.UTC(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate()
      )
    );
    const todayAtMidnightUTC = new Date(
      Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
    );
    const diffInMilliseconds =
      selectedDateUTC.getTime() - todayAtMidnightUTC.getTime();
    const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);
    return diffInDays >= 0 && diffInDays < count;
  }

  // Function to submit all collected data
  const submitAllData = async () => {
    // Assuming scheduleData is your state that includes all the form data
    const {
      hasInsurance,
      insuranceCarrierName,
      insurancePhoneNumber,
      groupNumber,
      memberIdNumber,
      employerName,
      policyHolderFirstName,
      policyHolderLastName,
      policyHolderDob,
      policyHolderZip,
      appointmentNotes,
      birthDate,
      email,
      firstName,
      gender,
      lastName,
      mobilePhone,
      isPatientPolicyHolder,
      otherInsuranceCarrierName,
    } = scheduleData;

    // Use the first selected slot for submission
    const firstSelectedSlot = selectedSlot?.length > 0 ? selectedSlot[0] : null;

    let slot_type;

    if (
      selectedOption === "New Patient" &&
      bookingFor === "Adult" &&
      selectedService === "cleaning"
    ) {
      slot_type = "new";
    } else if (
      selectedOption === "New Patient" &&
      bookingFor === "Child" &&
      selectedService === "childCleaning"
    ) {
      slot_type = "child";
    } else if (
      selectedOption === "Existing Patient" &&
      bookingFor === "Adult" &&
      selectedService === "cleaning"
    ) {
      slot_type = "recall";
    } else if (
      selectedOption === "Existing Patient" &&
      bookingFor === "Child" &&
      selectedService === "childCleaning"
    ) {
      slot_type = "recall_child";
    } else if (
      selectedOption === "Existing Patient" &&
      bookingFor === "Adult" &&
      selectedService === "limited_exam"
    ) {
      slot_type = "limited_exam";
    } else if (
      selectedOption === "New Patient" &&
      bookingFor === "Adult" &&
      selectedService === "limited_exam"
    ) {
      slot_type = "limited_exam";
    } else if (
      selectedOption === "New Patient" &&
      bookingFor === "Adult" &&
      selectedService == "consultory"
    ) {
      slot_type = "consult";
    } else if (
      selectedOption === "New Patient" &&
      bookingFor === "Adult" &&
      selectedService == "free_consult"
    ) {
      // Its own slot type, not the oral surgery consult above. The API books
      // both as a Consult in Open Dental; what differs is the rooms each can
      // land in.
      slot_type = "free_consult";
    }

    // Construct the note including the appointmentNotes and insurance information
    const grpNumb = groupNumber
      ? `Group Number: ${groupNumber}\n`
      : "Group Number: none\n";
    const memberIdNumb = memberIdNumber
      ? `Member ID Number: ${memberIdNumber}\n`
      : "Member ID Number: none\n";
    const employer = employerName
      ? `Employer Name: ${employerName}\n`
      : "Employer Name: none\n";
    const insuranceNum = insurancePhoneNumber
      ? `Insurance Phone Number: ${insurancePhoneNumber}\n`
      : "Insurance Phone Number: none\n";
    const otherInsuranceCarrier = otherInsuranceCarrierName
      ? `Other Insurance Carrier Name: ${otherInsuranceCarrierName}\n`
      : "";

    let note = `Added via [[Online]]\nDate Added: ${new Date().toLocaleDateString(
      "en-CA"
    )} ${new Date().toLocaleTimeString()}\n`;

    if (hasInsurance === "yes") {
      note += `Insurance: Yes\n Carrier Name: ${insuranceCarrierName}\n ${otherInsuranceCarrier} ${grpNumb} ${memberIdNumb} ${insuranceNum} ${employer}`;
    }
    if (hasInsurance === "no") {
      note += `Insurance: Unknown/Not Available\n`;
    }
    if (hasInsurance === "cash") {
      note += `Insurance: Cash\n`;
    }

    if (isPatientPolicyHolder === "no") {
      note += `Other Policy Holder Information:\n PolicyHolderFirstName ${policyHolderFirstName}\n PolicyHolderLastName ${policyHolderLastName}\n PolicyHolderDOB ${policyHolderDob}\n`;
    }
    note += `PT Notes: ${appointmentNotes}\nFirst: ${firstName}\nLast: ${lastName}\nEmail: ${email}\nGender: ${gender}\nDOB: ${birthDate}\nPhone: ${mobilePhone}\n[[Online]]`;
    const selectedLocation = locations?.find(
      (loc) => loc.id === parseInt(locID)
    );
    const clinicNum = selectedLocation?.clinicnum ?? location.clinicnum;
    const fullData = {
      Gender: gender,
      Note: note,
      apt_datetime: `${selectedDateSlot} ${selectedTimeSlot}`,
      birth_date: birthDate,
      email: email,
      first_name: firstName,
      clinic_num: clinicNum,
      last_name: lastName,
      op: firstSelectedSlot, // Include only the first selected slot
      slot_type: slot_type, // Add the mapped slot_type
      WirelessPhone: mobilePhone,
      // No ProvNum: the API resolves the provider from the insurance answer
      // below. This app never had a usable one to send. See MDW-182.
      // Sent so the backend resolves INSE when the SP does not include that provider code.
      ...(hasInsurance === "no" && { insuranceType: "INSE" }),
      insurance: {
        hasInsurance,
        ...(hasInsurance === "yes" && {
          insuranceName: insuranceCarrierName || null,
          insurancePhone: insurancePhoneNumber || null,
          groupNumber: groupNumber || null,
          memberIdNumber: memberIdNumber || null,
          employerName: employerName || null,
          homeZipCode: policyHolderZip || null,
          // OpenDental's policy holder fields are filled either way: with the
          // holder the form asked for, or with the patient when the patient
          // is the holder. Before, "yes" sent nothing and OD showed blanks.
          ...(isPatientPolicyHolder === "no"
            ? {
                policyHolderFirstName: policyHolderFirstName || null,
                policyHolderLastName: policyHolderLastName || null,
                policyHolderDob: policyHolderDob || null,
              }
            : {
                policyHolderFirstName: firstName || null,
                policyHolderLastName: lastName || null,
                policyHolderDob: birthDate || null,
              }),
        }),
      },
    };

    if (locationId) {
      try {
        setIsProcessing(true);

        if (selectedDateSlot && isWithinDays(selectedDateSlot, 14)) {
          const confirmedDefnum = await fetchConfirmCode(locationId);
          if (confirmedDefnum) {
            fullData.Confirmed = confirmedDefnum;
          }
        }

        const response = await fetch(
          `${API_BASE_URL}/${locID}/appointments`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(fullData),
          }
        );

        // A server error can come back as an HTML page, not JSON. Reading it
        // as JSON threw, and the patient was told nothing at all.
        const datas = await response.json().catch(() => ({}));
        console.log("response", datas);
        if (response.status !== 200) {
          // Only 403 and 422 used to be shown, so a 409 (the slot was taken
          // a moment ago) or a 500 left the patient on the form with no
          // message, or with the one from an earlier attempt. Our own 4xx
          // messages are written for patients; a 5xx message is not.
          setShowError(true);
          setIsSubmitted(false);
          setErrorMessage(
            response.status < 500 && datas.message
              ? datas.message
              : BOOKING_FAILED_MESSAGE
          );
        }
        if (response.status === 200) {
          setIsSubmitted(true);
          setShowError(false); // Show error when request fails
          setErrorMessage(""); // Clear any existing error messages

          // Call to send the event to the parent window
          window.top.postMessage(
            {
              type: "trackEvent",
              eventName: "scheduling-success",
              value: 3,
              mode: "reverse",
            },
            "*"
          );
        }

        // Handle successful submission (e.g., navigate to a confirmation page)
      } catch (error) {
        // The request never got an answer (connection dropped, request
        // blocked). This used to end silently, back on the form.
        console.error("Error submitting data:", error);
        setIsProcessing(false);
        setIsSubmitted(false);
        setShowError(true);
        setErrorMessage(BOOKING_FAILED_MESSAGE);
      } finally {
        setIsProcessing(false);
        // setIsSubmitted(true);
      }
    }
  };

  const handleSubmit = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    // Each attempt starts without the message of the one before.
    clearSubmitError();

    try {
      const token = await generateToken("submit");
      if (!token) {
        // This branch read validationData before it was declared, so the
        // patient saw a JavaScript error instead of a message.
        throw new Error(
          "We couldn't verify this request. Please reload the page and try again."
        );
      }

      // Fall back to null if the IP lookup is blocked (firewall, browser
      // extension, DNT); the backend derives IP from request headers instead.
      let ip = null;
      try {
        const ipResponse = await fetch("https://api64.ipify.org?format=json");
        const ipData = await ipResponse.json();
        ip = ipData.ip;
      } catch {
        // backend falls back to X-Forwarded-For / server-side IP
      }

      const validationResponse = await fetch(
        `${API_BASE_URL}/validate-recaptcha`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, expectedAction: "submit", ip }),
        }
      );

      const validationData = await validationResponse.json();

      if (validationResponse.status !== 200 || validationData.reason) {
        setShowError(true);
        setErrorMessage(
          validationData.message || "An unexpected error occurred."
        );
        throw new Error(validationData.message || "Validation failed.");
      }

      // The API counts submits per IP over 15 minutes. Production blocks
      // past two; the limit comes from the build so QA can turn it off.
      const configuredLimit = process.env.BOOKING_ATTEMPT_LIMIT;
      const attemptLimit =
        configuredLimit === "" || configuredLimit === undefined
          ? 2
          : Number(configuredLimit);
      if (attemptLimit > 0 && validationData.interactions_count > attemptLimit) {
        setShowError(true);
        setErrorMessage(
          "Too many interactions detected. Please try again later."
        );
        throw new Error(
          "Too many interactions detected. Booking request has been blocked."
        );
      }

      await submitAllData();
    } catch (error) {
      setShowError(true);
      setErrorMessage(error.message || "An unexpected error occurred.");
    } finally {
      setIsProcessing(false);
    }
  };

  const debouncedHandleSubmit = debounce(handleSubmit, 400);

  return (
    <>
      {!osIsLoading ? (
        <ProcessingComponent height={"40vh"} msg={"Loading data..."} />
      ) : (
        <>
          {!isSubmitted && !isProcessing && (
            // Nothing here submits natively: a button without a type, or
            // Enter in a field, would reload the page and lose the office
            // in the URL.
            <form className="details-form" onSubmit={(e) => e.preventDefault()}>
              {/* The dark stage carries the page title, the step indicator
                  and the white panel; the pattern runs behind all of it. */}
              <div className="ob-stage mint-pattern-leaves">
                <header className="ob-hero">
                  <h1 className="ob-hero__title">
                    {goToSchedule ? "Schedule" : "Details"}
                  </h1>
                  <div className="ob-stepper" aria-label="Progress">
                    <div className="ob-stepper__step">
                      <span
                        className={`ob-stepper__dot is-active ${
                          goToSchedule ? "" : "is-current"
                        }`}
                      />
                      <span className="ob-stepper__label">Details</span>
                    </div>
                    <span className="ob-stepper__line" />
                    <div className="ob-stepper__step">
                      <span
                        className={`ob-stepper__dot ${
                          goToSchedule ? "is-active is-current" : ""
                        }`}
                      />
                      <span className="ob-stepper__label">Schedule</span>
                    </div>
                  </div>
                </header>
                <main className="ob-panel">
                  <div className="ob-content">
                    {!goToSchedule && (
                      <>
                        {/* 1. Patient type and who it is for */}
                        <section className="ob-section">
                          <h2 className="ob-section__title">1. Select one</h2>
                          <div className="ob-grid ob-grid--2">
                            <button
                              type="button"
                              className={`ob-btn ${
                                selectedOption === "New Patient"
                                  ? "ob-btn--primary"
                                  : "ob-btn--outline"
                              }`}
                              aria-pressed={selectedOption === "New Patient"}
                              onClick={() =>
                                choose(handleOptionChange, "New Patient")
                              }
                            >
                              New Patient
                            </button>
                            {/* Oral surgery offices only take new patients */}
                            {location.clinicnum == 0 && (
                              <button
                                type="button"
                                className={`ob-btn ${
                                  selectedOption === "Existing Patient"
                                    ? "ob-btn--primary"
                                    : "ob-btn--outline"
                                }`}
                                aria-pressed={
                                  selectedOption === "Existing Patient"
                                }
                                onClick={() =>
                                  choose(handleOptionChange, "Existing Patient")
                                }
                              >
                                Existing Patient
                              </button>
                            )}
                          </div>
                          <p className="ob-label">Booking For:</p>
                          <div className="ob-grid ob-grid--2">
                            <button
                              type="button"
                              className={`ob-btn ${
                                bookingFor === "Adult"
                                  ? "ob-btn--primary"
                                  : "ob-btn--outline"
                              }`}
                              aria-pressed={bookingFor === "Adult"}
                              onClick={() =>
                                choose(handleBookingForChange, "Adult")
                              }
                            >
                              Adult (13+)
                            </button>
                            {location.clinicnum == 0 && (
                              <button
                                type="button"
                                className={`ob-btn ${
                                  bookingFor === "Child"
                                    ? "ob-btn--primary"
                                    : "ob-btn--outline"
                                }`}
                                aria-pressed={bookingFor === "Child"}
                                onClick={() =>
                                  choose(handleBookingForChange, "Child")
                                }
                              >
                                Child (12 and Under)
                              </button>
                            )}
                          </div>
                        </section>

                        {/* 2. Services */}
                        <section className="ob-section">
                          <h2 className="ob-section__title">
                            2. What can we help you with?
                          </h2>
                          <div className="ob-grid ob-grid--3">
                            {displayedServices
                              .filter((service) => {
                                // Oral surgery locations offer the consultation
                                // and nothing else, as they always have.
                                if (location.clinicnum !== 0) {
                                  return service.id === "consultory";
                                }
                                // General dentistry never sees that one, and
                                // sees the free consult only while Ops have it
                                // running.
                                if (service.id === "consultory") return false;
                                if (service.id === "free_consult") {
                                  return features.free_consult;
                                }
                                return true;
                              })
                              // Ops own the wording and rewrote it once within
                              // two hours of asking for the feature, so it
                              // comes from the API rather than from the bundle.
                              .map((service) =>
                                service.id === "free_consult" &&
                                features.free_consult_label
                                  ? {
                                      ...service,
                                      title: features.free_consult_label,
                                    }
                                  : service
                              )
                              .map((service) => (
                                <button
                                  type="button"
                                  key={service.id}
                                  className={`ob-btn ${
                                    selectedService === service.id
                                      ? "ob-btn--primary"
                                      : "ob-btn--outline"
                                  }`}
                                  aria-pressed={selectedService === service.id}
                                  onClick={() => handleServiceSelect(service.id)}
                                >
                                  {service.title}
                                </button>
                              ))}
                          </div>
                        </section>

                        {/* 3. Day and time, plus the offices nearby */}
                        <Suspense
                          fallback={<div className="ob-notice">Loading...</div>}
                        >
                          <RenderDateTimeSelection
                            noSlots={noSlots}
                            initialWeekStart={initialWeekStart}
                            selectedService={selectedService}
                            selectedSlots={selectedSlots}
                            locationId={locationId}
                            selectedOption={selectedOption}
                            setSelectedSlots={setSelectedSlots}
                            setSelectedDateSlot={setSelectedDateSlot}
                            setSelectedTimeSlot={setSelectedTimeSlot}
                            setSelectedSlot={setSelectedSlot}
                            setLocId={setLocId}
                            isSlotsLoading={isSlotsLoading}
                            nextWeek={nextWeek}
                            prevWeek={prevWeek}
                            currentWeekStart={currentWeekStart}
                            bookingFor={bookingFor}
                            locID={locID}
                          />
                        </Suspense>
                      </>
                    )}
                    {/* LAZY LOAD Schedule */}
                    <Suspense
                      fallback={<div className="ob-notice">Loading...</div>}
                    >
                      {isNextEnabled && goToSchedule && (
                        <div className="ob-schedule">
                        <Schedule
                          handleBackClick={handleBackClick}
                          callBackData={handleScheduleCallbackData}
                          showError={showError}
                          errorMessage={errorMessage}
                          selectedOption={selectedOption}
                          bookingFor={bookingFor}
                          selectedService={selectedService}
                          selectedTimeSlot={selectedTimeSlot}
                          selectedDateSlot={selectedDateSlot}
                          clinicnum={location.clinicnum}
                          savedData={scheduleData}
                        />
                        </div>
                      )}
                    </Suspense>
                  </div>
                </main>
              </div>
              {/* Next Button, below the stage as in the mockup */}
              {!isScheduleEnabled && (
                <div className="ob-next">
                  <button
                    type="button"
                    className="ob-btn ob-btn--primary"
                    disabled={!isNextEnabled}
                    onClick={handleNextClick}
                  >
                    Next
                  </button>
                </div>
              )}
              {/* Submit Button, the same button in the same place as Next */}
              {isScheduleEnabled && (
                <div className="ob-next">
                  <button
                    type="button"
                    disabled={!isSubmissionReady || isProcessing}
                    className="ob-btn ob-btn--primary"
                    onClick={debouncedHandleSubmit}
                  >
                    {isProcessing ? (
                      <>
                        <svg
                          className="w-5 h-5 mr-2 animate-spin"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                  {/* Google asks for this line wherever its badge is hidden. */}
                  <p className="ob-recaptcha-note">
                    This site is protected by reCAPTCHA and the Google{" "}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>{" "}
                    and{" "}
                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{" "}
                    apply.
                  </p>
                </div>
              )}
            </form>
          )}
          {/* LAZY LOAD Thank You */}
          <Suspense fallback={<div className="ob-notice">Loading...</div>}>
            {isSubmitted && !showError && (
              <ThankYou
                locationId={locationId}
                locID={locID}
                selectedDateSlot={selectedDateSlot}
                selectedTimeSlot={selectedTimeSlot}
              />
            )}
          </Suspense>
          {/* Loading component */}
          {isProcessing && !isSubmitted && !showError && (
            <ProcessingComponent
              height={"30vh"}
              msg={"Processing your request, please stand by..."}
            />
          )}
        </>
      )}
    </>
  );
}

export default Details;
